// ProblemSolution.js
import React from 'react'
import { motion } from 'framer-motion'
import { StaticImage } from 'gatsby-plugin-image'
import { HiSparkles, HiDocumentSearch, HiUserGroup, HiTemplate, HiClock, HiDownload } from 'react-icons/hi'

const ProblemSolution = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  }

  const features = [
    {
      title: "AI-Powered Resume Analysis",
      description: "Advanced AI algorithms analyze and optimize your resume content for maximum impact and relevance.",
      icon: <HiSparkles className="w-6 h-6" />,
      stats: "40% higher interview rate"
    },
    {
      title: "ATS-Friendly Templates",
      description: "Professionally designed templates that ensure your resume passes through Applicant Tracking Systems.",
      icon: <HiDocumentSearch className="w-6 h-6" />,
      stats: "95% ATS pass rate"
    },
    {
      title: "One-Click Document Generation",
      description: "Instantly generate tailored resumes, cover letters, and follow-up emails matching specific job descriptions.",
      icon: <HiUserGroup className="w-6 h-6" />,
      stats: "3x faster applications"
    }
  ]

  const additionalFeatures = [
    { icon: <HiTemplate />, label: "Smart Templates" },
    { icon: <HiClock />, label: "1-Click Generate" },
    { icon: <HiDownload />, label: "Multi-Format Export" }
  ]

  return (
    <div className="relative py-24 bg-white overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-20 right-0 w-96 h-96 bg-indigo-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
        <div className="absolute bottom-20 left-0 w-96 h-96 bg-purple-50 rounded-full mix-blend-multiply filter blur-xl opacity-70" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-pink-50 rounded-full mix-blend-multiply filter blur-xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div
          className="text-center mb-20"
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 bg-indigo-50 rounded-full px-4 py-2 mb-6">
            <HiSparkles className="w-5 h-5 text-indigo-600" />
            <span className="text-indigo-600 text-sm font-medium">
              One-Click Resume Magic
            </span>
          </span>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold text-neutral-900 leading-tight">
            From <span className="text-indigo-600">Job Description</span> to <span className="text-indigo-600">Perfect Application</span>
          </h2>
          <p className="mt-6 text-md text-neutral-700 max-w-2xl mx-auto leading-relaxed">
            Generate tailored resumes, cover letters, and follow-up emails instantly with our 
            AI-powered platform. Perfect match for every job application.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Features */}
          <motion.div
            className="space-y-8"
            variants={fadeInUp}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="group relative flex gap-6 p-8 rounded-2xl bg-white border border-neutral-100 shadow-sm hover:shadow-lg transition-all duration-300"
                whileHover={{ scale: 1.02 }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity" />
                <div className="relative flex gap-6">
                  <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    {feature.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-sm text-neutral-700 mb-3 leading-relaxed">
                      {feature.description}
                    </p>
                    <span className="text-sm font-medium text-indigo-600">
                      {feature.stats}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Additional Features Bar */}
            <motion.div
              className="flex justify-between items-center mt-8 p-6 bg-neutral-50 rounded-xl"
              variants={fadeInUp}
            >
              {additionalFeatures.map((item, index) => (
                <div key={index} className="flex flex-col items-center gap-2">
                  <div className="text-indigo-600 text-xl">
                    {item.icon}
                  </div>
                  <span className="text-sm text-neutral-700">{item.label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right Column - Interactive Demo */}
          <motion.div
            className="relative lg:pl-8"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative bg-gradient-to-b from-indigo-50 to-white p-8 rounded-2xl">
              <div className="absolute inset-0 bg-white/50 backdrop-blur-sm rounded-2xl" />
              <div className="relative">
                <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <StaticImage
                    src="../images/product-oneclick.png"
                    alt="ResumeFlex Interface Demo"
                    className="rounded-lg"
                    placeholder="blurred"
                  />
                </div>

                {/* Interactive Elements */}
                <div className="flex gap-4">
                  <motion.button
                    className="flex-1 bg-indigo-600 text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors shadow-md"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.open("https://app.resumeflex.com", "_blank")}
                  >
                    Start Building Now
                  </motion.button>
                  <motion.button
                    className="flex-1 bg-white text-neutral-700 px-6 py-3 rounded-lg text-sm font-medium border border-neutral-200 hover:bg-neutral-50 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Watch Tutorial
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Floating Stats Cards */}
            <motion.div
              className="absolute -right-4 -bottom-4 bg-white rounded-lg shadow-lg p-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-sm text-neutral-700">
                <div className="font-semibold text-indigo-600">90% Faster</div>
                <div>Resume Creation</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default ProblemSolution
